import {
    Box,
    Divider,
    Heading,
    Icons,
    List,
    ListItem,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import EmptyState from "components/empty-state";
import { ExperienceViewItemCard } from "components/experiences/experience-view-item-card";
import type { ExperienceCardLayout } from "components/pages/experience-view/experience-view-utils";
import { RichTextRenderer } from "components/rich-text-renderer";
import ShowMoreText from "components/show-more-text/show-more-text";
import { useTranslation } from "react-i18next";
import { AvailableTimesSection } from "types/available-times";
import { AvailabilityState } from "utils/availability-utils";
import { ExperienceViewItemType } from "types/experience-view";
import type {
    ExperienceViewExperienceItem,
    ExperienceViewSectionItem,
} from "types/experience-view";
import { resolveRichTextField, type RichTextField } from "types/rich-text-field";
import type { WebRule } from "types/web-rule";

interface BookedEverythingMessageProps {
    isActivityBooking: boolean;
    sectionsNotBookedYet: ExperienceViewSectionItem[];
    experiencesNotBookedYet: ExperienceViewExperienceItem[];
    hasBookedItems: boolean;
    webRules?: WebRule[];
}

function BookedEverythingMessage({
    isActivityBooking,
    sectionsNotBookedYet,
    experiencesNotBookedYet,
    hasBookedItems,
    webRules = [],
}: BookedEverythingMessageProps) {
    const { t } = useTranslation();
    const isAllBookedActivities =
        isActivityBooking &&
        sectionsNotBookedYet.length === 0 &&
        experiencesNotBookedYet.length === 0;
    const isAllBookedExperienceBooking =
        !isActivityBooking &&
        experiencesNotBookedYet.length === 0 &&
        webRules.length === 0 &&
        hasBookedItems;

    if (!isAllBookedActivities && !isAllBookedExperienceBooking) return null;

    return (
        <EmptyState
            icon={Icons.Smile} // Todo: change to PartyHorn when available
            description={t("selectActivity.noActivityDescription")}
            header={t("selectActivity.noActivityHeader")}
            pt={ThemeSpaceVariable.None}
        />
    );
}

interface ExperienceGridProps {
    experiences: ExperienceViewExperienceItem[];
    layouts: ExperienceCardLayout[];
    availability: Map<number, AvailabilityState>;
    renderOnlyExperience?: boolean;
    onExperienceClick: (experience: ExperienceViewExperienceItem) => void;
}

function ExperienceGrid({
    experiences,
    layouts,
    availability,
    renderOnlyExperience = false,
    onExperienceClick,
}: ExperienceGridProps) {
    if (experiences.length === 0) return null;

    return (
        <Box
            display="grid"
            gridTemplateColumns="repeat(2, 1fr)"
            columnGap={ThemeSpaceVariable.Medium}
        >
            {experiences.map((exp, i) => {
                const layout = layouts[i];
                return (
                    <Box
                        key={exp.id}
                        gridColumn={layout === "experience-full" ? "1 / -1" : undefined}
                        minW={0}
                    >
                        <ExperienceViewItemCard
                            item={exp}
                            layout={layout}
                            isLastItem={i === experiences.length - 1}
                            renderOnlyExperience={renderOnlyExperience}
                            availabilityState={availability.get(exp.id)}
                            onClick={() => onExperienceClick(exp)}
                        />
                    </Box>
                );
            })}
        </Box>
    );
}

interface SectionListProps {
    sections: ExperienceViewSectionItem[];
    availability: Map<number, AvailabilityState>;
    divider?: boolean;
    onSectionClick: (section: ExperienceViewSectionItem) => void;
}

function SectionList({
    sections,
    availability,
    divider = false,
    onSectionClick,
}: SectionListProps) {
    if (sections.length === 0) return null;

    return (
        <List>
            {divider && <Divider />}
            {sections.map((section) => (
                <ListItem key={section.id}>
                    <ExperienceViewItemCard
                        item={section}
                        layout="section"
                        availabilityState={availability.get(section.id)}
                        onClick={() => onSectionClick(section)}
                    />
                </ListItem>
            ))}
        </List>
    );
}

interface WebRuleListProps {
    webRules: WebRule[];
    sections: ExperienceViewSectionItem[];
    divider?: boolean;
    onWebRuleClick: (webRuleAsSection: AvailableTimesSection) => void;
}

function WebRuleList({ webRules, sections, divider = false, onWebRuleClick }: WebRuleListProps) {
    if (webRules.length === 0) return null;

    return (
        <List>
            {divider && <Divider />}
            {webRules.map((rule) => {
                const section = sections.find((s) => s.id === rule.sectionId);
                // An addition (text row) carries the restaurant's message rather than a bookable
                // time set, so its card presents the activity instead: the section's name and
                // description, with the message itself shown inside the modal. The section's
                // description wins even when empty; only an unknown section falls back to the rule,
                // so a card is never left unlabelled.
                const name = (rule.isTextRow && section?.name) || rule.title;
                const description = rule.isTextRow
                    ? (section?.description ?? rule.comment)
                    : rule.comment;
                const ruleItem: ExperienceViewSectionItem = {
                    type: ExperienceViewItemType.Section,
                    id: rule.id,
                    sectionIds: [rule.sectionId],
                    activeDates: [],
                    name,
                    description,
                };
                // Map the webrule to a bookable section
                const webRuleAsSection = new AvailableTimesSection({
                    timeSets: [rule],
                    name,
                    description,
                    id: rule.id,
                    image: section?.imageReference16x9 || "",
                });
                return (
                    <ListItem key={rule.id}>
                        <ExperienceViewItemCard
                            item={ruleItem}
                            layout="section"
                            onClick={() => onWebRuleClick(webRuleAsSection)}
                        />
                    </ListItem>
                );
            })}
        </List>
    );
}

interface IntroductionMessageProps {
    header?: string | null;
    message?: string | null;
    template?: RichTextField | null;
}

/**
 * Location-level introduction shown at the top of the experience view when
 * the user has not yet picked a date. Renders the header and/or the message
 * depending on which fields the restaurant configured.
 *
 * The rich template authored in the new settings view wins over the legacy string, so the
 * text follows the guest's language choice. As in select-guests.tsx, the legacy header is
 * dropped once a template exists — the rich content carries its own heading, and the header
 * has no rich counterpart to translate.
 */
function IntroductionMessage({ header, message, template }: IntroductionMessageProps) {
    const { i18n } = useTranslation();

    if (!header && !message && !template) return null;

    return (
        <>
            {header && !template && (
                <Heading
                    as="h1"
                    fontSize={ThemeFontSizeVariable.X2Large}
                    mb={ThemeSpaceVariable.Small}
                >
                    {header}
                </Heading>
            )}
            {(template || message) && (
                <Box mb={ThemeSpaceVariable.Medium}>
                    {template ? (
                        // blockContent: ShowMoreText clamps inside Casper's Text by default, which
                        // is a <p>, and RichTextRenderer emits block-level content — so the clamp
                        // has to be a Box here to keep the DOM valid.
                        <ShowMoreText
                            initialLines={3}
                            backgroundColor={ThemeColorVariable.Background}
                            blockContent
                        >
                            <RichTextRenderer
                                content={resolveRichTextField(template, i18n.language)}
                            />
                        </ShowMoreText>
                    ) : (
                        <ShowMoreText
                            text={message!}
                            initialLines={3}
                            backgroundColor={ThemeColorVariable.Background}
                        />
                    )}
                </Box>
            )}
        </>
    );
}

export { BookedEverythingMessage, ExperienceGrid, IntroductionMessage, SectionList, WebRuleList };
